import { SectionHeading } from "@/components/shared/SectionHeading";
import { RevealStagger, RevealItem, Reveal } from "@/components/shared/Reveal";
import { UserCheck, FileCheck2, ShieldCheck, Wallet, ArrowRightLeft, Link2, PackageCheck, ShieldAlert, Lock, ScrollText, QrCode } from "lucide-react";

const TRUST_CARDS = [
  { icon: UserCheck, title: "Driver Verification", copy: "Verify applicable driver qualifications before an assignment begins." },
  { icon: FileCheck2, title: "Credential Verification", copy: "Confirm license, CDL, and other required credentials on file." },
  { icon: ShieldCheck, title: "Insurance Requirements", copy: "Verify applicable insurance information for the vehicle and assignment." },
  { icon: Wallet, title: "Secured Payment Status", copy: "Show drivers when required compensation has been secured." },
  { icon: ArrowRightLeft, title: "Documented Handoffs", copy: "Record every relay handoff between drivers with a timestamped log." },
  { icon: Link2, title: "Chain of Custody", copy: "Track custody of the load or vehicle from pickup through delivery." },
  { icon: PackageCheck, title: "Delivery Verification", copy: "Confirm and document that a delivery was completed as assigned." },
  { icon: ShieldAlert, title: "Fraud Prevention", copy: "Screen for fake identities, falsified credentials, and fraudulent listings." },
  { icon: Lock, title: "Account Security", copy: "Protect accounts with verification and ongoing account monitoring." },
];

const INSPECTION_INFO = ["Driver identity", "CDL information", "Medical qualification status", "Responsible motor carrier", "USDOT", "Truck / Trailer", "Active assignment", "Insurance status", "HOS summary", "Connected ELD provider"];

export const SafetyTrust = () => {
  return (
    <section id="trust" data-testid="safety-trust-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading eyebrow="Safety & Trust" title="Built Around Trust." testId="safety-trust" />

        <RevealStagger className="mt-14 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {TRUST_CARDS.map((c) => (
            <RevealItem key={c.title}>
              <div data-testid={`trust-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-center border border-rr-line rounded-2xl p-6 h-full hover:border-rr-orange/50 hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-full bg-rr-surface mx-auto flex items-center justify-center text-rr-navy mb-4">
                  <c.icon size={20} />
                </div>
                <h4 className="font-heading font-bold text-sm text-rr-navy">{c.title}</h4>
                <p className="text-xs text-rr-muted mt-2 leading-relaxed">{c.copy}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>

        <p className="text-center text-xs text-rr-muted mt-10 max-w-3xl mx-auto">
          RelayRigs is a technology marketplace in development. RelayRigs does not represent itself as a government agency, FMCSA-approved ELD, insurer or licensed escrow provider unless and until the applicable relationship or authorization exists.
        </p>

        <div className="mt-24 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start bg-rr-navy rounded-[2rem] p-8 md:p-12 relative overflow-hidden">
          <div className="grain-overlay" />
          <div className="relative">
            <div className="flex items-center gap-2 text-rr-orangeLight text-xs font-bold tracking-[0.2em] uppercase mb-4">
              <QrCode size={14} /> Roadside Inspection
            </div>
            <h3 className="font-heading text-3xl font-bold text-white">Inspection Information When It's Needed.</h3>
            <p className="mt-4 text-white/60 text-sm leading-relaxed max-w-md">
              RelayRigs Inspection Mode helps drivers organize information and connect to appropriate ELD inspection workflows. It does not replace FMCSA or law enforcement systems.
            </p>
          </div>
          <Reveal delay={0.15}>
            <div className="relative grid grid-cols-2 gap-2.5">
              {INSPECTION_INFO.map((i) => (
                <div key={i} data-testid={`inspection-info-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2.5 text-xs font-semibold text-white/80">
                  {i}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
